import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './BottomSheet.css';

export interface BottomSheetProps {
  /** Whether the bottom sheet is open */
  isOpen: boolean;
  /** Callback when bottom sheet should close */
  onClose: () => void;
  /** Title to display in the header */
  title?: string;
  /** Content to display in the bottom sheet */
  children: React.ReactNode;
  /** Custom className for the bottom sheet */
  className?: string;
  /** Maximum height as percentage of viewport (default: 90) */
  maxHeight?: number;
}

/**
 * BottomSheet - A mobile-friendly drawer component that slides up from the bottom
 * On desktop, appears as a centered modal. On mobile, slides up from bottom.
 * 
 * Features:
 * - Smooth slide-up animation
 * - Backdrop overlay with close on outside click
 * - Prevents body scroll when open
 * - Keyboard support (Escape to close)
 * - Focus trap for accessibility
 * - Responsive sizing (full-width mobile, centered desktop)
 * 
 * @example
 * ```tsx
 * <BottomSheet 
 *   isOpen={isOpen} 
 *   onClose={() => setIsOpen(false)}
 *   title="What is Kairos?"
 * >
 *   <KairosNameExplanation />
 * </BottomSheet>
 * ```
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  maxHeight = 90,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const previousBodyOverflow = useRef<string>('');
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Prevent body scroll when bottom sheet is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      previousBodyOverflow.current = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Restore previous overflow style
      document.body.style.overflow = previousBodyOverflow.current;
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = previousBodyOverflow.current;
    };
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle backdrop click to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };

  // Handle swipe down to close (mobile)
  const handleTouchStart = useRef<{ y: number; time: number } | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (sheetRef.current && e.touches.length === 1) {
      const touch = e.touches[0];
      handleTouchStart.current = {
        y: touch.clientY,
        time: Date.now(),
      };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!handleTouchStart.current || !sheetRef.current) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - handleTouchStart.current.y;

    // Only allow downward swipe
    if (deltaY > 0) {
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!handleTouchStart.current || !sheetRef.current) return;

    const touch = e.changedTouches[0];
    const deltaY = touch.clientY - handleTouchStart.current.y;
    const deltaTime = Date.now() - handleTouchStart.current.time;
    const velocity = Math.abs(deltaY / deltaTime);

    // Reset transform
    sheetRef.current.style.transform = '';

    // Close if swiped down significantly or with sufficient velocity
    if (deltaY > 100 || (deltaY > 50 && velocity > 0.3)) {
      onClose();
    }

    handleTouchStart.current = null;
  };

  if (!isOpen) return null;

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(
    <div
      ref={backdropRef}
      className={`bottom-sheet-backdrop ${isOpen ? 'bottom-sheet-backdrop-open' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'bottom-sheet-title' : undefined}
    >
      <div
        ref={sheetRef}
        className={`bottom-sheet ${isOpen ? 'bottom-sheet-open' : ''} ${className}`}
        style={{ maxHeight: `${maxHeight}vh` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle for mobile */}
        <div className="bottom-sheet-handle" />

        {/* Header */}
        {title && (
          <div className="bottom-sheet-header">
            <h2 id="bottom-sheet-title" className="bottom-sheet-title">
              {title}
            </h2>
            <button
              className="bottom-sheet-close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              ×
            </button>
          </div>
        )}

        {/* Content */}
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

